import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { duasAPI, categoriesAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';

const DuaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    arabic: '',
    transliteration: '',
    translation: '',
    benefits: '',
    reference: '',
    display_order: 0,
    is_published: true,
  });

  useEffect(() => {
    fetchCategories();
    if (isEdit) fetchDua();
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getDuaCategories();
      if (response.data.success) setCategories(response.data.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

const fetchDua = async () => {
  try {
    const response = await duasAPI.getById(id); // ✅ FIX: Use specific endpoint
    if (response.data.success && response.data.data) {
      const dua = response.data.data;
      setFormData({
        category_id: dua.category_id || '',
        title: dua.title || '',
        arabic: dua.arabic || '',
        transliteration: dua.transliteration || '',
        translation: dua.translation || '',
        benefits: dua.benefits || '',
        reference: dua.reference || '',
        display_order: dua.display_order || 0,
        is_published: dua.is_published ?? true,
      });
    }
  } catch (error) {
    console.error('Failed to fetch dua:', error);
    setToast({ show: true, message: 'Failed to load dua', type: 'error' });
  } finally {
    setLoading(false);
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isEdit) {
        await duasAPI.update(id, formData);
        setToast({ show: true, message: 'Dua updated successfully', type: 'success' });
      } else {
        await duasAPI.create(formData);
        setToast({ show: true, message: 'Dua created successfully', type: 'success' });
      }
      setTimeout(() => navigate('/duas'), 1500);
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to save dua', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading dua..." />;

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

      <div className="mb-4">
        <button className="btn btn-outline-secondary" onClick={() => navigate('/duas')}>
          <i className="bi bi-arrow-left me-2"></i>Back to Duas
        </button>
      </div>

      <div className="stat-card">
        <h4 className="mb-4">{isEdit ? 'Edit Dua' : 'Create New Dua'}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row g-4">
            <div className="col-md-6">
              <label className="form-label">Title *</label>
              <input type="text" className="form-control" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Category *</label>
              <select className="form-select" value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })} required>
                <option value="">Select category</option>
                {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
              </select>
            </div>
            <div className="col-12">
              <label className="form-label">Arabic Text *</label>
              <textarea className="form-control" rows="3" value={formData.arabic} onChange={(e) => setFormData({ ...formData, arabic: e.target.value })} required style={{ direction: 'rtl', textAlign: 'right' }}></textarea>
            </div>
            <div className="col-12">
              <label className="form-label">Transliteration</label>
              <textarea className="form-control" rows="2" value={formData.transliteration} onChange={(e) => setFormData({ ...formData, transliteration: e.target.value })}></textarea>
            </div>
            <div className="col-12">
              <label className="form-label">Translation *</label>
              <textarea className="form-control" rows="3" value={formData.translation} onChange={(e) => setFormData({ ...formData, translation: e.target.value })} required></textarea>
            </div>
            <div className="col-12">
              <label className="form-label">Benefits</label>
              <textarea className="form-control" rows="2" value={formData.benefits} onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}></textarea>
            </div>
            <div className="col-md-6">
              <label className="form-label">Reference</label>
              <input type="text" className="form-control" value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder="e.g., Sahih Bukhari" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Display Order</label>
              <input type="number" className="form-control" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })} min="0" />
            </div>
            <div className="col-12">
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="is_published" checked={formData.is_published} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} />
                <label className="form-check-label" htmlFor="is_published">Publish immediately</label>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : <><i className="bi bi-save me-2"></i>{isEdit ? 'Update Dua' : 'Create Dua'}</>}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/duas')}>Cancel</button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuaForm;